<template>
  <cv-date-picker
    ref="picker"
    v-bind="$attrs"
    :model-value="value"
    :pattern="CALENDAR_DATE_PATTERN"
    @update:model-value="handleChange"
  >
    <template v-for="slot in Object.keys(slots)" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData || {}" />
    </template>
  </cv-date-picker>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, useSlots, useTemplateRef } from "vue";
import { CvDatePickerComponent } from "@plugins/common/types.ts";
import { CALENDAR_DATE_PATTERN } from "@modules/calendar/event/service/calendar.constant.ts";

// Props
interface Props {
  value?: string | null;
}

const { value = null } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  change: [value: string];
}>();

// Composables
const slots = useSlots();

// Template refs
const picker = useTemplateRef<CvDatePickerComponent>("picker");

// Reactive data
const dataValue = ref<string>(value as string);

// Methods
const handleChange = (newValue: string) => {
  // to prevent emitting change event on load
  if (dataValue.value !== newValue) {
    dataValue.value = newValue;
    emit("change", dataValue.value);
  }
};

const handleInputBlur = (event: FocusEvent) => {
  const target = event.target as HTMLInputElement;
  dataValue.value = target.value;
  emit("change", dataValue.value);
};

// Lifecycle hooks
onMounted(() => {
  const datePicker = picker.value;
  datePicker?.$refs?.date?.addEventListener("blur", handleInputBlur);
});

onBeforeUnmount(() => {
  const datePicker = picker.value;
  datePicker?.$refs?.date?.removeEventListener("blur", handleInputBlur);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.cv-date-picker {
  .bx--date-picker__icon {
    transform: translateY(-50%) scale(1.5);
    path {
      fill: $interactive-01;
    }
  }

  .bx--form-requirement {
    &::before {
      display: none;
    }
  }
  &.disabled {
    .bx--date-picker-input__wrapper {
      > .bx--date-picker__input {
        background-color: $disabled-01;
        color: $disabled-02;
      }

      &::after {
        opacity: 0.5;
        filter: none;
      }

      .bx--date-picker__icon {
        path {
          fill: $disabled-02;
        }
      }
    }
  }

  .bx--date-picker-input__wrapper {
    ~ .bx--form-requirement {
      @include carbon--type-style("helper-text-01", true);
    }
  }

  .bx--date-picker,
  .bx--date-picker-container,
  .bx--date-picker__input {
    max-width: 100%;
  }

  .bx--date-picker-container {
    .bx--label {
      @include carbon--type-style("label-01", true);
      color: $text-02;
    }
  }

  .bx--date-picker--single {
    .bx--date-picker__input {
      width: auto;
    }
  }

  .bx--date-picker__input {
    @include carbon--type-style("body-long-01", true);
    display: inline-flex;
    border-radius: 10px;
    border: 1px solid $ui-03;
    cursor: pointer;

    &::placeholder {
      color: $text-03;
    }

    &.flatpickr-mobile {
      ~ .bx--date-picker__icon {
        visibility: hidden;
      }
    }

    &[data-invalid] {
      outline: none;
    }

    &[data-invalid="true"] {
      border: 1px solid $state-negative;
    }
  }

  .bx--date-picker-input__wrapper {
    ~ .bx--form-requirement {
      @include carbon--type-style("helper-text-01", true);
    }
  }
}

.flatpickr-calendar {
  &.open {
    &.bx--date-picker__calendar {
      // to overwrite boxshadow bg colour
      box-shadow: 0 2px 6px #0003;
    }
  }
}
</style>
